import { useEffect, useState } from "react";
import { Download, FlaskConical, RotateCcw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGame } from "@/context/GameContext";
import { api } from "@/lib/api";
import { LAYERS } from "@/lib/layers";
import { t } from "@/lib/translations";

const eventTypeColors = {
  game_start: "#60A5FA",
  attempt_correct: "#4ADE80",
  attempt_wrong: "#F87171",
  game_win: "#FACC15",
  game_exit: "#94A3B8",
};

const eventLabels = {
  id: {
    game_start: "Mulai",
    attempt_correct: "Benar",
    attempt_wrong: "Salah",
    game_win: "Menang",
    game_exit: "Keluar",
  },
  en: {
    game_start: "Start",
    attempt_correct: "Correct",
    attempt_wrong: "Wrong",
    game_win: "Win",
    game_exit: "Exit",
  },
};

export default function ResearchTab({ profiles }) {
  const { language, researchMode, toggleResearchMode } = useGame();
  const [selectedChild, setSelectedChild] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    if (profiles && profiles.length && !selectedChild) setSelectedChild(profiles[0]);
  }, [profiles, selectedChild]);

  useEffect(() => {
    if (!selectedChild) {
      setSummary(null);
      setRecentEvents([]);
      return;
    }
    api.getResearchSummary(selectedChild.id).then(setSummary).catch(() => {});
    api.listResearchEvents(selectedChild.id, 50).then(setRecentEvents).catch(() => {});
  }, [selectedChild]);

  const refresh = () => {
    if (!selectedChild) return;
    api.getResearchSummary(selectedChild.id).then(setSummary).catch(() => {});
    api.listResearchEvents(selectedChild.id, 50).then(setRecentEvents).catch(() => {});
  };

  const downloadCsv = () => {
    if (!selectedChild) return;
    const url = api.researchCsvUrl(selectedChild.id);
    window.open(url, "_blank");
    toast.success(language === "id" ? "Mengunduh CSV..." : "Downloading CSV...");
  };

  const resetData = async () => {
    if (!selectedChild) return;
    const ok = window.confirm(
      language === "id"
        ? `Hapus semua data penelitian untuk ${selectedChild.name}?`
        : `Delete all research data for ${selectedChild.name}?`
    );
    if (!ok) return;
    try {
      await api.resetResearchEvents(selectedChild.id);
      toast.success(language === "id" ? "Data dihapus" : "Data cleared");
      refresh();
    } catch (err) {
      console.warn("Failed to reset research events:", err);
      toast.error("Error");
    }
  };

  return (
    <div className="space-y-4" data-testid="research-tab-content">
      {/* Mode toggle banner */}
      <Card className="rounded-3xl border-4 border-violet-200 bg-white">
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center flex-shrink-0">
              <FlaskConical className="w-6 h-6 text-violet-600" strokeWidth={3} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="font-display font-bold text-xl text-slate-800">
                  {language === "id" ? "Mode Penelitian" : "Research Mode"}
                </h3>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-bold text-slate-600">
                    {researchMode
                      ? language === "id" ? "Aktif" : "On"
                      : language === "id" ? "Nonaktif" : "Off"}
                  </Label>
                  <Switch
                    checked={researchMode}
                    onCheckedChange={toggleResearchMode}
                    data-testid="research-mode-toggle"
                  />
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                {language === "id"
                  ? "Saat aktif, setiap interaksi anak (mulai, benar, salah, menang, keluar) dicatat dengan timestamp untuk keperluan riset PAUD/skripsi/tesis. Data dapat diekspor sebagai CSV."
                  : "When enabled, each child interaction (start, correct, wrong, win, exit) is logged with timestamp for early-childhood research. Data can be exported as CSV."}
              </p>
              {researchMode && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {language === "id"
                      ? "Etika riset: pastikan informed consent dari orang tua/wali sebelum mengumpulkan data anak."
                      : "Research ethics: ensure informed consent from parents/guardians before collecting child data."}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {profiles.length === 0 ? (
        <Card className="rounded-3xl bg-white/70">
          <CardContent className="p-10 text-center text-slate-500 font-bold">
            {t("pd_no_children", language)}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Child selector */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Select
              value={selectedChild?.id || ""}
              onValueChange={(v) => {
                const c = profiles.find((x) => x.id === v);
                setSelectedChild(c);
              }}
            >
              <SelectTrigger className="rounded-2xl bg-white max-w-sm" data-testid="research-select-child">
                <SelectValue placeholder={t("pd_select_child", language)} />
              </SelectTrigger>
              <SelectContent>
                {profiles.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} ({c.age} {c.age === 1 ? t("year", language) : t("years", language)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={refresh}
                variant="outline"
                className="rounded-2xl"
                data-testid="research-refresh"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                {language === "id" ? "Segarkan" : "Refresh"}
              </Button>
              <Button
                onClick={downloadCsv}
                disabled={!summary || summary.total_events === 0}
                className="rounded-2xl bg-violet-500 hover:bg-violet-600 text-white shadow-[0_4px_0_#6d28d9]"
                data-testid="research-download-csv"
              >
                <Download className="w-4 h-4 mr-1" />
                {language === "id" ? "Ekspor CSV" : "Export CSV"}
              </Button>
              <Button
                onClick={resetData}
                variant="outline"
                className="rounded-2xl border-red-300 text-red-600 hover:bg-red-50"
                data-testid="research-reset"
              >
                {language === "id" ? "Reset" : "Reset"}
              </Button>
            </div>
          </div>

          {/* Summary stats */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="rounded-3xl border-4 border-violet-200 bg-white">
                <CardContent className="p-4">
                  <div className="text-xs font-bold text-slate-500 uppercase">
                    {language === "id" ? "Total Event" : "Total Events"}
                  </div>
                  <div className="font-display text-3xl font-bold text-violet-600">
                    {summary.total_events}
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-3xl border-4 border-sky-200 bg-white">
                <CardContent className="p-4">
                  <div className="text-xs font-bold text-slate-500 uppercase">
                    {language === "id" ? "Sesi" : "Sessions"}
                  </div>
                  <div className="font-display text-3xl font-bold text-sky-600">
                    {summary.sessions}
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-3xl border-4 border-green-200 bg-white">
                <CardContent className="p-4">
                  <div className="text-xs font-bold text-slate-500 uppercase">
                    {language === "id" ? "Percobaan" : "Attempts"}
                  </div>
                  <div className="font-display text-3xl font-bold text-green-600">
                    {summary.total_attempts}
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-3xl border-4 border-amber-200 bg-white">
                <CardContent className="p-4">
                  <div className="text-xs font-bold text-slate-500 uppercase">
                    {language === "id" ? "Akurasi" : "Accuracy"}
                  </div>
                  <div className="font-display text-3xl font-bold text-amber-600">
                    {summary.accuracy !== null && summary.accuracy !== undefined
                      ? `${Math.round(summary.accuracy * 100)}%`
                      : "—"}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Per-game breakdown */}
          {summary && Object.keys(summary.by_game || {}).length > 0 && (
            <Card className="rounded-3xl bg-white border-4 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-display text-base">
                  {language === "id" ? "Rincian per Permainan" : "Per-Game Breakdown"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(summary.by_game).map(([game, counts]) => (
                  <div
                    key={game}
                    className="flex items-center justify-between p-2 rounded-2xl bg-slate-50"
                  >
                    <div className="font-bold text-slate-700">{t(`g_${game}`, language)}</div>
                    <div className="flex gap-2 flex-wrap text-xs">
                      {Object.entries(counts).map(([type, n]) => (
                        <span
                          key={type}
                          className="px-2 py-0.5 rounded-full text-white font-bold"
                          style={{ background: eventTypeColors[type] || "#94A3B8" }}
                        >
                          {eventLabels[language][type] || type}: {n}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Recent events log */}
          <Card className="rounded-3xl bg-white border-4 border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-base">
                {language === "id" ? "Event Terakhir" : "Recent Events"}{" "}
                <span className="text-xs font-normal text-slate-500">
                  ({recentEvents.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentEvents.length === 0 ? (
                <div className="text-center text-slate-400 py-6 text-sm">
                  {language === "id"
                    ? researchMode
                      ? "Belum ada data. Anak harus bermain dengan Mode Penelitian aktif."
                      : "Aktifkan Mode Penelitian terlebih dahulu, lalu biarkan anak bermain."
                    : researchMode
                    ? "No data yet. Have the child play with Research Mode enabled."
                    : "Enable Research Mode first, then have the child play."}
                </div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {recentEvents.slice(-50).reverse().map((e) => {
                    const layerInfo = LAYERS[e.layer];
                    return (
                      <div
                        key={e.id}
                        className="flex items-center gap-2 text-xs p-2 rounded-xl bg-slate-50"
                        data-testid={`event-row-${e.id}`}
                      >
                        <span
                          className="px-1.5 py-0.5 rounded-full text-white font-bold flex-shrink-0"
                          style={{ background: eventTypeColors[e.event_type] || "#94A3B8" }}
                        >
                          {eventLabels[language][e.event_type] || e.event_type}
                        </span>
                        <span className="font-bold text-slate-700">
                          L{e.layer} · {t(`g_${e.game_key}`, language)}
                        </span>
                        <span className="text-slate-400 ml-auto font-mono">
                          {new Date(e.timestamp).toLocaleTimeString(
                            language === "id" ? "id-ID" : "en-US",
                            { hour: "2-digit", minute: "2-digit", second: "2-digit" }
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="text-xs text-slate-500 italic text-center py-2">
            {language === "id"
              ? "ⓘ CSV kompatibel dengan Excel/SPSS. Kolom: event_id, session_id, child_id, child_name, child_age, layer, game_key, event_type, timestamp, metadata."
              : "ⓘ CSV compatible with Excel/SPSS. Columns: event_id, session_id, child_id, child_name, child_age, layer, game_key, event_type, timestamp, metadata."}
          </div>
        </>
      )}
    </div>
  );
}
